import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu as MenuIcon } from '@/utils/icons';
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext';
import { useTutorials } from '@/contexts/TutorialsContext';
import SidebarRail from './SidebarRail';
import PhonePromptModal, { shouldShowPhonePrompt } from '@/components/settings/PhonePromptModal';

const AppShellInner: React.FC = () => {
  const { isMobileDrawerOpen, openMobileDrawer, closeMobileDrawer } = useSidebar();
  const { activeTutorial } = useTutorials();
  const location = useLocation();
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);

  useEffect(() => {
    if (!activeTutorial && shouldShowPhonePrompt()) setShowPhonePrompt(true);
  }, [activeTutorial]);

  // Close drawer on route change
  useEffect(() => {
    closeMobileDrawer();
  }, [location.pathname, closeMobileDrawer]);

  // ESC key closes drawer
  useEffect(() => {
    if (!isMobileDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileDrawerOpen, closeMobileDrawer]);

  // Lock body scroll while drawer open
  useEffect(() => {
    if (!isMobileDrawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileDrawerOpen]);

  // Close drawer when viewport crosses past the mobile breakpoint (e.g. tablet
  // rotation or desktop window resize). Without this, the CSS query hides the
  // drawer visually but `isMobileDrawerOpen` stays true and the body
  // scroll-lock effect keeps the page unscrollable.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(min-width: 769px)');
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) closeMobileDrawer();
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [closeMobileDrawer]);

  return (
    <div className="cgm-app-shell">
      <button
        type="button"
        onClick={openMobileDrawer}
        className="cgm-app-mobile-hamburger"
        aria-label="Open navigation"
      >
        <MenuIcon className="w-5 h-5" />
      </button>

      {isMobileDrawerOpen && (
        <div
          className="cgm-rail-backdrop"
          onClick={closeMobileDrawer}
          aria-hidden
        />
      )}

      <SidebarRail />

      <div className="cgm-app-content">
        <Outlet />
      </div>

      {showPhonePrompt && !activeTutorial && (() => {
        try {
          const u = JSON.parse(localStorage.getItem('user_info') || '{}');
          return (
            <PhonePromptModal
              userId={u.id}
              onClose={() => setShowPhonePrompt(false)}
            />
          );
        } catch { return null; }
      })()}
    </div>
  );
};

const AppShell: React.FC = () => (
  <SidebarProvider>
    <AppShellInner />
  </SidebarProvider>
);

export default AppShell;
